import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CommunicationBridge } from '@sequelae/lore-mfe-kit';
import { Button, Typography, TextInput, Spinner } from '@sequelae/lore-core';
import { WheelDatePicker, WheelDatePickerProps } from '../../components/WheelDatePicker';
import { postMessageService } from '../../services/postMessage';
import { useNavigationMode } from '../../contexts/NavigationModeContext';
import { NavigationModeToggle } from '../../components/NavigationModeToggle';
import { NavigationTestButtons } from '../../components/NavigationTestButtons';
import AccountService from '../../services/AccountService';
import type { DemographicUpdate } from '../../services/AccountService';
// import type { AccountData } from '../../services/AccountService';
import VerificationService from '../../services/VerificationService';
import type { VerificationData } from '../../services/VerificationService';
import '../../styles/accountInfo.css';
import '../../styles/shared-styles.css';
import '../../styles/ios-styles.css';
import { getRefUserId } from '../../utils/getRefUserId';

interface FormData {
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: string;
  phoneNumber: string;
  useForShipping: boolean;
  shippingName: string;
  streetAddress: string;
  addressLine2: string;
  city: string;
  state: string;
  zipcode: string;
}

// interface DemographicData {
//   ref_demographic_id: number;
//   value: string;
// }

// Move Input component outside
const Input = memo(
  ({
    label,
    value,
    onChange,
    placeholder,
    type = 'text',
    isLast = false,
    onFocus,
    id,
    disabled = false,
    readOnly = false,
  }: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    type?: string;
    isLast?: boolean;
    onFocus?: () => void;
    id: string;
    disabled?: boolean;
    readOnly?: boolean;
  }) => (
    <div
      className="ios-list-item"
      style={{
        borderBottom: isLast ? 'none' : '0.5px solid rgba(0, 0, 0, 0.15)',
      }}
    >
      <label htmlFor={id} className="ios-list-item-text flex-shrink-0">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        onFocus={onFocus}
        disabled={disabled}
        readOnly={readOnly || disabled}
        className="ios-input"
      />
    </div>
  )
);

Input.displayName = 'Input';

// Move Section component outside
const Section = memo(({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="mb-4">
    <div className="ios-section-title">{title}</div>
    <div className="ios-card" style={{ margin: '0 var(--spacing-space-4)' }}>
      {children}
    </div>
  </div>
));

Section.displayName = 'Section';

const WheelDatePickerWrapper = ({ _value, _onChange }: WheelDatePickerProps) => {
  // Unused props are intentionally ignored
  return null;
};

WheelDatePickerWrapper.displayName = 'WheelDatePickerWrapper';

const AccountInformationIOS = memo(() => {
  const navigate = useNavigate();
  const { isNativeMode } = useNavigationMode();

  // Get ref_user_id priority: URL > localStorage > token > default
  const urlParams = new URLSearchParams(window.location.search);
  const urlUserId = urlParams.get('ref_user_id');
  const localStorageUserId = window.localStorage.getItem('ref_user_id');
  const tokenUserId = getRefUserId();

  const refUserId = urlUserId
    ? parseInt(urlUserId, 10)
    : localStorageUserId
    ? parseInt(localStorageUserId, 10)
    : tokenUserId
    ? tokenUserId
    : 44016;
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [verificationData, setVerificationData] = useState<VerificationData | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    middleName: '',
    lastName: '',
    dateOfBirth: '',
    phoneNumber: '',
    useForShipping: true,
    shippingName: '',
    streetAddress: '',
    addressLine2: '',
    city: '',
    state: '',
    zipcode: '',
  });

  // Convert YYYY-MM-DD from API to MMM DD YYYY for display
  const convertApiDateToDisplay = useCallback((apiDate: string) => {
    if (!apiDate) return '';
    try {
      // Extract date part if it's an ISO string
      const datePart = apiDate.split('T')[0];
      const date = new Date(datePart + 'T00:00:00Z');
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const month = monthNames[date.getUTCMonth()];
      const day = date.getUTCDate();
      const year = date.getUTCFullYear();
      return `${month} ${day} ${year}`;
    } catch (err) {
      console.error('Error converting API date:', err);
      return apiDate;
    }
  }, []);

  // Convert MMM DD YYYY to MM/DD/YYYY for the date picker
  const convertDisplayToPickerFormat = useCallback((displayDate: string) => {
    if (!displayDate) return '';
    try {
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const parts = displayDate.split(' ');
      if (parts.length !== 3) return displayDate;
      const monthIndex = monthNames.indexOf(parts[0]);
      if (monthIndex === -1) return displayDate;
      const month = (monthIndex + 1).toString().padStart(2, '0');
      const day = parts[1].padStart(2, '0');
      const year = parts[2];
      return `${month}/${day}/${year}`;
    } catch (err) {
      console.error('Error converting display date to picker format:', err);
      return displayDate;
    }
  }, []);

  // Convert MMM DD YYYY to YYYY-MM-DD for the API
  const convertDisplayToApiFormat = useCallback((displayDate: string) => {
    if (!displayDate) return '';
    try {
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const parts = displayDate.split(' ');
      if (parts.length !== 3) return displayDate;
      const monthIndex = monthNames.indexOf(parts[0]);
      if (monthIndex === -1) return displayDate;
      const month = (monthIndex + 1).toString().padStart(2, '0');
      const day = parts[1].padStart(2, '0');
      const year = parts[2];
      return `${year}-${month}-${day}`;
    } catch (err) {
      console.error('Error converting display date to API format:', err);
      return displayDate;
    }
  }, []);

  // Convert MM/DD/YYYY from picker to MMM DD YYYY for display
  const convertPickerToDisplayFormat = useCallback((pickerDate: string) => {
    if (!pickerDate) return '';
    try {
      const [month, day, year] = pickerDate.split('/');
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const monthName = monthNames[parseInt(month) - 1];
      return `${monthName} ${parseInt(day)} ${year}`;
    } catch (err) {
      console.error('Error converting picker date to display format:', err);
      return pickerDate;
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setError(null);
        console.log('[MenuMFE] Fetching account data for user:', refUserId);
        const data = await AccountService.fetchAccountData(refUserId);
        console.log('[MenuMFE] Account data fetched successfully:', data);

        setFormData(prev => ({
          ...prev,
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          middleName: data.middleName || '',
          dateOfBirth: convertApiDateToDisplay(data.dateOfBirth || ''),
          phoneNumber: data.phone || '',
          useForShipping: true,
          shippingName: `${data.firstName || ''} ${data.lastName || ''}`.trim(),
        }));
      } catch (err) {
        console.error('[MenuMFE] Failed to fetch account data:', err);
        setError('Unable to load account data. You can still enter your information.');
        // Keep the form empty but don't prevent rendering
        setFormData({
          firstName: '',
          middleName: '',
          lastName: '',
          dateOfBirth: '',
          phoneNumber: '',
          useForShipping: true,
          shippingName: '',
          streetAddress: '',
          addressLine2: '',
          city: '',
          state: '',
          zipcode: '',
        });
      }
    };

    fetchData();
  }, [refUserId, convertApiDateToDisplay]);

  // Fetch verification data on mount
  useEffect(() => {
    const fetchVerificationData = async () => {
      try {
        console.log('[MenuMFE] Fetching verification data');
        const data = await VerificationService.getVerificationStatus();
        console.log('[MenuMFE] Verification data fetched:', data);
        setVerificationData(data);
      } catch (err) {
        console.error('[MenuMFE] Failed to fetch verification data:', err);
        // Don't show error - verification data is optional
      }
    };

    fetchVerificationData();
  }, []);

  // Get email and username from JWT token
  useEffect(() => {
    const token = CommunicationBridge.getAccessToken();
    if (!token) return;

    try {
      const decodedToken = JSON.parse(atob(token.split('.')[1]));
      setUserEmail(decodedToken.email || decodedToken.emails?.[0] || '');
      setUserName(decodedToken.user_name || '');
    } catch (e) {
      console.error('Error decoding token:', e);
    }
  }, []);

  const handleChange = useCallback(
    (field: string, value: string | boolean) => {
      setFormData(prev => ({
        ...prev,
        [field]: value,
        ...(field === 'useForShipping' && value === true
          ? {
              shippingName: `${formData.firstName} ${formData.lastName}`.trim(),
            }
          : {}),
      }));

      // If it's a demographic field, debounce the update
      if (['firstName', 'lastName', 'middleName', 'dateOfBirth', 'phoneNumber'].includes(field)) {
        setSaveStatus('saving');

        // Clear any existing timeout to debounce
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }

        // Debounce the API call - wait 1 second after user stops typing
        saveTimeoutRef.current = setTimeout(() => {
          // Convert dateOfBirth from display format (MMM DD YYYY) to API format (YYYY-MM-DD)
          const dateForApi =
            field === 'dateOfBirth'
              ? convertDisplayToApiFormat(value as string)
              : convertDisplayToApiFormat(formData.dateOfBirth);

          const demographics: DemographicUpdate[] = [
            {
              ref_demographic_id: 1,
              ref_user_id: refUserId,
              demographic_value: field === 'firstName' ? (value as string) : formData.firstName,
            },
            {
              ref_demographic_id: 2,
              ref_user_id: refUserId,
              demographic_value: field === 'lastName' ? (value as string) : formData.lastName,
            },
            {
              ref_demographic_id: 3,
              ref_user_id: refUserId,
              demographic_value: field === 'middleName' ? (value as string) : formData.middleName,
            },
            {
              ref_demographic_id: 7,
              ref_user_id: refUserId,
              demographic_value: dateForApi,
            },
            {
              ref_demographic_id: 10,
              ref_user_id: refUserId,
              demographic_value: field === 'phoneNumber' ? (value as string) : formData.phoneNumber,
            },
          ];

          AccountService.updateAccount(demographics, refUserId)
            .then(() => {
              setSaveStatus('saved');
              // Reset to idle after 2 seconds
              saveTimeoutRef.current = setTimeout(() => {
                setSaveStatus('idle');
              }, 2000);
            })
            .catch((err: Error) => {
              console.error('Failed to update account:', err);
              setError(err instanceof Error ? err.message : 'Failed to update account');
              setSaveStatus('idle');
            });
        }, 1000); // Wait 1 second after user stops typing
      }
    },
    [formData, refUserId, convertDisplayToApiFormat]
  );

  // No-op function for read-only fields
  const noOpChange = useCallback(() => {
    // Read-only field, no action needed
  }, []);

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length === 0) return '';
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  // Format verification birth date from YYYY-MM-DD or ISO format to MMM DD YYYY
  const formatVerificationDate = (dateString: string | undefined) => {
    if (!dateString) return '';
    try {
      // Extract date part if it's an ISO string (remove time portion)
      const datePart = dateString.split('T')[0];
      // Create a date object (add time to ensure UTC interpretation)
      const date = new Date(datePart + 'T00:00:00Z');
      // Format as MMM DD YYYY (e.g., "Jun 15 1978")
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const month = monthNames[date.getUTCMonth()];
      const day = date.getUTCDate();
      const year = date.getUTCFullYear();
      return `${month} ${day} ${year}`;
    } catch (err) {
      console.error('Error formatting verification date:', err);
      return dateString;
    }
  };

  return (
    <div className="screen ios">
      <div className="shared-container">
        <div className="ios-container">
          <header className="ios-header">
            {!isNativeMode && (
              <Button onClick={() => navigate(-1)} className="ios-back-button" variant="text">
                <svg
                  width="13"
                  height="21"
                  viewBox="0 0 13 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 2L3 10.5L10 19"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                <Typography variant="body" className="ml-auto">
                  Menu
                </Typography>
              </Button>
            )}
            <Typography variant="h1" className="ios-header-title">
              Account Information
            </Typography>
            <div className="flex items-center gap-2 ml-auto px-2">
              {saveStatus !== 'idle' && (
                <Typography
                  variant="caption"
                  className={`font-medium ${
                    saveStatus === 'saving' ? 'text-secondary' : 'text-success'
                  }`}
                >
                  {saveStatus === 'saving' ? 'Saving...' : 'Saved ✓'}
                </Typography>
              )}
              <NavigationModeToggle />
            </div>
          </header>

          <div className="py-4">
            {error && (
              <div
                className="text-error p-4 text-center"
                style={{ marginBottom: 'var(--spacing-space-4)' }}
              >
                <Typography variant="body" color="error">
                  {error}
                </Typography>
              </div>
            )}

            <Section title="Account information">
              <Input
                id="email"
                label="Email"
                value={userEmail}
                onChange={noOpChange}
                placeholder=""
                type="email"
                readOnly
              />
              <Input
                id="username"
                label="Username"
                value={userName}
                onChange={noOpChange}
                placeholder=""
                isLast
                readOnly
              />
            </Section>

            <Section title="Personal information">
              <Input
                id="firstName"
                label="First name"
                value={formData.firstName}
                onChange={value => handleChange('firstName', value)}
                placeholder="First name"
              />
              <Input
                id="middleName"
                label="Middle name"
                value={formData.middleName}
                onChange={value => handleChange('middleName', value)}
                placeholder="Middle name"
              />
              <Input
                id="lastName"
                label="Last name"
                value={formData.lastName}
                onChange={value => handleChange('lastName', value)}
                placeholder="Last name"
              />
              <Input
                id="dateOfBirth"
                label="Date of birth"
                value={formData.dateOfBirth}
                onChange={value => handleChange('dateOfBirth', value)}
                placeholder="MMM DD YYYY"
                type="text"
                onFocus={() => setShowDatePicker(true)}
                readOnly
              />
              <Input
                id="phoneNumber"
                label="Phone number"
                value={formData.phoneNumber}
                onChange={value => handleChange('phoneNumber', formatPhoneNumber(value))}
                placeholder="(000) 000 - 0000"
                isLast
              />
            </Section>

            <div
              className="ios-card flex items-center gap-3"
              style={{
                margin: '0 var(--spacing-space-4) var(--spacing-space-4)',
                padding: '11px var(--spacing-space-4)',
              }}
            >
              <div
                onClick={() => handleChange('useForShipping', !formData.useForShipping)}
                className={`ios-checkbox-custom ${
                  formData.useForShipping ? 'checked' : 'unchecked'
                }`}
              >
                {formData.useForShipping && (
                  <svg
                    width="12"
                    height="9"
                    viewBox="0 0 18 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.6 10.6L1.4 6.4L0 7.8L5.6 13.4L17.6 1.4L16.2 0L5.6 10.6Z"
                      fill="white"
                    />
                  </svg>
                )}
              </div>
              <Typography variant="body" className="ios-list-item-text">
                Use this name for shipping address
              </Typography>
            </div>

            <Section title="Shipping Address">
              {!formData.useForShipping && (
                <Input
                  id="shippingName"
                  label="Name"
                  value={formData.shippingName}
                  onChange={value => handleChange('shippingName', value)}
                  placeholder="Name"
                />
              )}
              <Input
                id="streetAddress"
                label="Street Address"
                value={formData.streetAddress}
                onChange={value => handleChange('streetAddress', value)}
                placeholder="Address"
              />
              <Input
                id="addressLine2"
                label="Address line 2"
                value={formData.addressLine2}
                onChange={value => handleChange('addressLine2', value)}
                placeholder="Unit, apt., suite etc."
              />
              <Input
                id="city"
                label="City"
                value={formData.city}
                onChange={value => handleChange('city', value)}
                placeholder="City"
              />
              <Input
                id="state"
                label="State"
                value={formData.state}
                onChange={value => handleChange('state', value)}
                placeholder="State"
              />
              <Input
                id="zipcode"
                label="Zipcode"
                value={formData.zipcode}
                onChange={value => handleChange('zipcode', value)}
                placeholder="Zipcode"
                isLast
              />
            </Section>

            <Section title="Verification information">
              <Input
                id="verificationFirstName"
                label="First name"
                value={verificationData?.input_first_name || ''}
                onChange={noOpChange}
                placeholder=""
                readOnly
              />
              <Input
                id="verificationLastName"
                label="Last name"
                value={verificationData?.input_last_name || ''}
                onChange={noOpChange}
                placeholder=""
                readOnly
              />
              <Input
                id="verificationDateOfBirth"
                label="Date of birth"
                value={
                  verificationData ? formatVerificationDate(verificationData.input_birth_date) : ''
                }
                onChange={noOpChange}
                placeholder=""
                readOnly
              />
              <Input
                id="verificationEmail"
                label="Email address"
                value={verificationData?.input_email || ''}
                onChange={noOpChange}
                placeholder=""
                isLast
                readOnly
              />
            </Section>
          </div>
          <div className="navigation-test-spacer">
            <NavigationTestButtons />
          </div>
        </div>
      </div>

      {showDatePicker && (
        <WheelDatePicker
          value={convertDisplayToPickerFormat(formData.dateOfBirth)}
          onChange={(date: string) => {
            handleChange('dateOfBirth', convertPickerToDisplayFormat(date));
            setShowDatePicker(false);
          }}
          onClose={() => setShowDatePicker(false)}
        />
      )}
    </div>
  );
});

AccountInformationIOS.displayName = 'AccountInformationIOS';

export { AccountInformationIOS };
